import { CurrencyPipe } from '@angular/common';
import { autoserialize, autoserializeAs } from 'cerialize';
import * as moment from 'moment';

import { Address, IAddress } from './address';
import { BaseDTO, IBaseDTO } from './base-dto';
import { Charity, ICharity } from './charity';
import { Contact, IContact } from './contact';
import { Courier } from './courier';
import { CourierType, isRoadie } from './courier-type';
import { DonationDiscount, IDonationDiscount } from './donation-discount';
import { DonationItem, IDonationItem } from './donation-item';
import { DonationPayment, IDonationPayment } from './donation-payment';
import { Fee } from './fee';
import { OwnerType } from './owner';
import { PartOfDay } from './part-of-day';
import { IPartner, Partner } from './partner';
import { IPaymentDetails, PaymentDetails } from './payment-details';
import { IPricing, Pricing } from './pricing';
import { Quote } from './quote';
import { ISpecification, Specification } from './specification';
import { SponsorshipAlgorithm } from './sponsorship-algorithm';
import { CharityStore, ICharityStore } from './store';
import { ITruck, Truck } from './truck';

export enum DonationVersion {
  _1_0 = '1.0',
  _1_1 = '1.1',
  _1_2 = '1.2',
  _1_3 = '1.3',
  _1_4 = '1.4', //start new lead form
}

export enum DonationLeadType {
  LEAD = 'Lead',
  DONATION = 'Donation',
}

export abstract class BaseDonationLead extends BaseDTO {
  type: DonationLeadType;
  @autoserialize id?: string;

  @autoserialize version?: DonationVersion;

  @autoserializeAs('created_at') createdAt?: string;
  @autoserializeAs('updated_at') updatedAt?: string;
  @autoserializeAs('expires_at') expiresAt?: string;

  @autoserialize date?: string;
  @autoserializeAs('part_of_day') partOfDay?: PartOfDay;
  @autoserialize hour_slot?: number | null;
  @autoserializeAs(Contact, 'donor') donor: Contact;
  @autoserializeAs(Address, 'address') address: Address;
  @autoserialize instructions?: string;

  @autoserializeAs(Charity, 'charity') charity?: Charity;
  @autoserializeAs('charity_id') charityId?: string | null;
  @autoserializeAs(CharityStore, 'store') store?: CharityStore | null;
  @autoserializeAs('store_id') storeId?: string;

  @autoserializeAs(Partner, 'partner') partner?: Partner | null;
  @autoserializeAs('partner_id') partnerId?: string | null;
  @autoserializeAs(Truck, 'vehicle') vehicle?: Truck | null;
  @autoserializeAs('vehicle_id') vehicleId?: string | null;

  @autoserialize courier?: Courier;

  @autoserializeAs(Fee, 'fee') fee?: Fee | null;
  @autoserializeAs(Pricing, 'pricing') pricing: Pricing;
  @autoserialize gratuity: number;

  @autoserialize quote?: Quote;

  @autoserializeAs(PaymentDetails, 'discount_payment_details')
  discountPaymentDetails?: PaymentDetails | null; // total taken from charity
  @autoserializeAs(PaymentDetails, 'payment_details')
  paymentDetails?: PaymentDetails | null; //total taken donor

  @autoserializeAs(DonationPayment, 'payment') payment: DonationPayment;

  @autoserializeAs(Specification, 'specification') specification: Specification;

  @autoserialize discount?: DonationDiscount | null;
  @autoserialize sponsorship_algorithm?: SponsorshipAlgorithm;

  @autoserializeAs('item_ids') itemIds?: string[];
  @autoserializeAs('donation_items') donationItems?: string | null;

  @autoserializeAs('owner_type') ownerType?: OwnerType;
  @autoserializeAs('owner_id') ownerId?: string;

  @autoserialize items?: DonationItem[] | null;
  aggregatedItems?: { item: DonationItem; count: number }[] | null;

  constructor(payload: IBaseDonationLead) {
    super(payload);
    this.id = payload?.id?.toString();

    this.version = payload?.version;

    this.createdAt = payload?.createdAt;
    this.updatedAt = payload?.updatedAt;
    this.expiresAt = payload?.expiresAt;

    this.date = payload?.date;
    this.partOfDay = payload?.partOfDay;
    this.hour_slot = payload?.hour_slot;
    this.donor = payload?.donor ? new Contact(payload?.donor) : (payload?.donor as Contact);
    this.address = payload?.address ? new Address(payload?.address) : (payload?.address as Address);
    this.instructions = payload?.instructions;

    this.charity = payload?.charity ? new Charity(payload?.charity) : undefined;
    this.charityId = payload?.charityId?.toString() || this.charity?.id || null;
    this.store = payload?.store ? new CharityStore(payload?.store) : undefined;
    this.storeId = payload?.storeId?.toString() || this.store?.id;

    this.partner = payload?.partner ? new Partner(payload?.partner) : undefined;
    this.partnerId = payload?.partnerId?.toString() || this?.partner?.id || null;
    this.vehicle = payload?.vehicle ? new Truck(payload.vehicle) : undefined;
    this.vehicleId = payload?.vehicleId?.toString() || this.vehicle?.id;

    this.courier = payload?.courier ? new Courier(payload.courier) : undefined;

    this.fee = payload?.fee ? new Fee(payload?.fee) : undefined;
    this.pricing = payload?.pricing ? new Pricing(payload?.pricing) : payload?.pricing;
    this.gratuity = payload?.gratuity;

    this.quote = payload?.quote ? new Quote(payload.quote) : undefined;

    this.discountPaymentDetails = payload?.discountPaymentDetails ? new PaymentDetails(payload?.discountPaymentDetails) : undefined;
    this.paymentDetails = payload?.paymentDetails ? new PaymentDetails(payload?.paymentDetails) : undefined;

    this.payment = payload?.payment ? new DonationPayment(payload?.payment) : (payload?.payment as DonationPayment);

    this.specification = payload?.specification ? new Specification(payload?.specification) : (payload?.specification as Specification);

    this.discount = payload?.discount ? new DonationDiscount(payload.discount) : undefined;
    this.sponsorship_algorithm = payload?.sponsorship_algorithm ? new SponsorshipAlgorithm(payload?.sponsorship_algorithm) : undefined;

    this.itemIds = payload?.itemIds?.map((id) => id.toString());
    this.donationItems = payload?.donationItems;

    this.ownerType = payload?.ownerType;
    this.ownerId = payload?.ownerId?.toString();

    this.items = payload?.items ? payload.items.map((item) => new DonationItem(item)) : null;

    this.aggregatedItems =
      this.items?.reduce(
        (acc, item) => {
          const existing = acc.find((i) => i.item.id === item.id);

          if (existing) {
            existing.count += 1;
          } else {
            acc.push({ item, count: 1 });
          }
          return acc;
        },
        [] as { item: DonationItem; count: number }[],
      ) || [];
  }

  public static OnSerialized(instance: BaseDonationLead, json: any): void {
    if (json.gratuity) json.gratuity = Pricing.formatPrice(json.gratuity * 100);
  }

  public static OnDeserialized(instance: BaseDonationLead): void {
    if (instance.gratuity) instance.gratuity = Pricing.formatPrice(instance.gratuity / 100);
    if (instance.items) {
      instance.items.forEach((item) => {
        item.price = Pricing.formatPrice(item.price / 100);
      });
    }
  }

  public get zip(): string | undefined {
    return this.address?.zip;
  }

  public get state(): string | undefined {
    return this.address?.state;
  }

  public get phone(): string {
    return this.donor?.phone;
  }

  public get fullAddress(): string {
    const addr =
      (this.address?.street ? this.address?.street + ', ' : '') +
      (this.address?.city ? this.address?.city + ', ' : '') +
      (this.address?.state ? this.address?.state + ', ' : '') +
      (this.address?.zip ? this.address?.zip : '') +
      ((this.address?.secondary?.length || 0) > 0 ? ' #' + this.address?.secondary : '');
    return addr.length > 0 ? addr : '/';
  }

  public get mapBoxAddress(): string {
    return this.address?.mapBoxAddress;
  }

  get isPast(): boolean {
    return moment().hour(0).minute(0).second(0).millisecond(0).isAfter(moment(this.date));
  }

  public getFee(): Fee | undefined | null {
    return this.fee || this.partner?.fee;
  }

  get originalQuote() {
    if (this.quote) {
      if (!this.isPartnerPickup && this.paymentDetails)
        return this.paymentDetails.total_amount + (this.discountPaymentDetails?.total_amount || 0);
      return this.quote?.total_amount;
    }
    return Pricing.getTotalPriceFormatted(this.specification, this.pricing);
  }

  get originalQuoteFuelFee() {
    return Pricing.formatPrice(this.originalQuote - this.originalQuote / (1 + this.pricing.fuelFee / 100));
  }

  get originalSponsoredAmount(): number {
    if (this.quote?.discount_amount >= 0) {
      if (!this.isPartnerPickup && this.discountPaymentDetails) return this.discountPaymentDetails?.total_amount;
      if (this.quote?.discount_amount >= 0) return this.quote?.discount_amount;
    }
    return Pricing.calculateSponsoredAmount(
      this.specification,
      this.pricing,
      this.discount,
      this.sponsorship_algorithm,
      this.specification.small,
    );
  }

  abstract get total(): number;
  abstract get totalSponsoredAmount(): number;
  abstract get fuelFee(): number;
  abstract get isLead(): boolean;
  abstract get isDonation(): boolean;

  totalAmount(currency: CurrencyPipe): string {
    return currency.transform(this.total) || '';
  }

  abstract get donatableSmall(): number;

  //#region PickupType
  get isUberPickup(): boolean {
    return this.quote?.courier === CourierType.UBER;
  }

  get isRoadiePickup(): boolean {
    return isRoadie(this.quote?.courier);
  }

  get isCourierPickup(): boolean {
    return this.isUberPickup || this.isRoadiePickup;
  }

  get isPartnerPickup(): boolean {
    return !this.quote || this.quote?.courier === CourierType.PARTNER;
  }
  //#endregion PickupType

  isMinVersion(version: DonationVersion): boolean {
    const v = Number(version.replaceAll('.', ''));
    const current = Number((this.version || '0').replaceAll('.', ''));
    return current >= v;
  }
}

export interface IBaseDonationLead extends IBaseDTO {
  id?: string;

  version?: DonationVersion;

  createdAt?: string;
  updatedAt?: string;
  expiresAt?: string | null;

  date?: string;
  partOfDay?: PartOfDay;
  hour_slot?: number | null;
  donor: IContact;
  address: IAddress;
  instructions?: string;

  charity?: ICharity;
  charityId?: string | null;
  store?: ICharityStore | null;
  storeId?: string | null;

  partner?: IPartner | null;
  partnerId?: string | null;
  vehicle?: ITruck | null;
  vehicleId?: string | null;

  courier?: Courier;

  fee?: Fee | null;
  pricing: IPricing;
  gratuity: number;

  quote?: Quote;

  discountPaymentDetails?: IPaymentDetails | null;
  paymentDetails?: IPaymentDetails | null;

  payment?: IDonationPayment;

  specification: ISpecification;

  discount?: IDonationDiscount | null;
  sponsorship_algorithm?: SponsorshipAlgorithm;

  itemIds?: string[];
  donationItems?: string | null;

  ownerType?: OwnerType;
  ownerId?: string;

  items?: IDonationItem[] | null;
}
